import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../../design-system/components/Card';
import SearchBar from './SearchBar';
import { Pagination } from './Pagination';
import { Skeleton, SkeletonCard } from './Skeleton';
import { EmptyState } from './EmptyState';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[4]};
`;
const FiltersSection = styled.div`
  display: flex;
  gap: ${spacing[4]};
  flex-wrap: wrap;
  flex: 1;
`;
const SortSelect = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
`;
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${spacing[4]};
`;
export type SortDirection = 'asc' | 'desc';
export interface SortConfig {
  field: string;
  direction: SortDirection;
}
export interface ListViewProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  loading?: boolean;
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  sortConfig?: SortConfig;
  onSortChange?: (config: SortConfig) => void;
  sortOptions?: { value: string; label: string }[];
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  viewMode?: 'cards' | 'table';
  showPagination?: boolean;
  showSorting?: boolean;
}
export function ListView<T>({
  items,
  renderItem,
  loading = false,
  emptyMessage,
  emptyAction,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  sortConfig,
  onSortChange,
  sortOptions,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  viewMode = 'cards',
  showPagination = true,
  showSorting = true,
}: ListViewProps<T>) {
  const { t } = useTranslation();
  const resolvedEmptyMessage: string =
    emptyMessage ?? ((t('common.noItems') as string) || 'No items found');
  const resolvedSearchPlaceholder: string =
    searchPlaceholder ?? ((t('common.searchPlaceholder') as string) || 'Search...');
  if (loading) {
    return (
      <Container>
        {' '}
        <CardsGrid>
          {' '}
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}{' '}
        </CardsGrid>{' '}
      </Container>
    );
  }
  if (items.length === 0) {
    return (
      <Container>
        <EmptyState title={resolvedEmptyMessage}>
          {emptyAction}
        </EmptyState>
      </Container>
    );
  }
  return (
    <Container>
      {' '}
      <Header>
        {' '}
        <FiltersSection>
          {' '}
          {onSearchChange && (
            <SearchBar
              value={searchValue || ''}
              onChange={onSearchChange}
              placeholder={resolvedSearchPlaceholder}
              fullWidth={false}
            />
          )}{' '}
          {showSorting && sortOptions && onSortChange && (
            <SortSelect
              value={sortConfig?.field || ''}
              onChange={e => {
                if (e.target.value) {
                  onSortChange({
                    field: e.target.value,
                    direction: sortConfig?.direction || 'asc',
                  });
                }
              }}
            >
              {' '}
              <option value="">Sort by...</option>{' '}
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}{' '}
            </SortSelect>
          )}{' '}
        </FiltersSection>{' '}
      </Header>{' '}
      {viewMode === 'cards' ? (
        <CardsGrid>
          {' '}
          {items.map((item, index) => (
            <React.Fragment key={index}> {renderItem(item)} </React.Fragment>
          ))}{' '}
        </CardsGrid>
      ) : (
        <div>{items.map((item, index) => renderItem(item))}</div>
      )}{' '}
      {showPagination && onPageChange && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={!!onPageSizeChange}
        />
      )}{' '}
    </Container>
  );
}
export default ListView;
